import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { EffectComposer, Bloom, ChromaticAberration, Vignette } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import { getCardById } from '@plotjack/core';
import { layerVertex, layerFragment } from './layerShader';
import { loadTex, glowTexture, blankTex, preload } from './fxTextures';
import { Particles } from './Particles';
import { layerSet, depthUrl, propArt, overlayUrl, backgroundUrl, type SceneLight } from '../../art/assets';
import { sceneFx, type PropPlacement } from '../../art/sceneFx';
import type { QualityProfile } from '../../art/quality';
import { getCardTexture } from '../../art/cardTexture';
import { scenePlaceholderSvg } from '../../art/placeholders';
import { figureUrl, type FigureDef } from '../../art/figures';

/** Image aspect of the painted plates. */
const IA = 1.5;
const FOV = 38;
const TAN = Math.tan(THREE.MathUtils.degToRad(FOV / 2));
const OVERSCAN = 1.15;
/** Distance from the camera for each painted layer, back to front. Kept fairly close together so
 * neighbouring cutouts never slide far enough apart to expose a seam (the old 3.7 front plane moved
 * almost 18% of the screen width at full tilt). */
const DIST4 = [17, 11.5, 8.2, 6.4];
/** Max lateral camera travel (world units) at full pointer or gyro tilt, before the aspect clamp. */
const LOOK_AMP = 0.3;
const LOOK_AMP_SHOWPIECE = 0.26;
const IMPACT_D = 6.4;

export interface FxState {
  impactAt: number;
  impactUv: THREE.Vector2;
  push: number;
  light: { current: number };
  flicker: number;
}

export interface LayeredSceneProps {
  sceneId: string;
  mood: string;
  dread: number;
  reducedMotion: boolean;
  quality: QualityProfile;
  look: { current: { x: number; y: number } };
  /** changes on every card play */
  playKey?: string;
  playedCardId?: string;
  overlayIds?: string[];
  dramatic?: boolean;
  /** title screen: slower, wider moves */
  showpiece?: boolean;
  onImpact?: () => void;
  /** where the played card lands, plate uv with a top-left origin */
  targetUv?: [number, number];
  /** painted layer the target sits on (0 backdrop .. 3 nearest) */
  targetLayer?: number;
  /** a DOM pin to keep glued to a plate point through camera motion */
  track?: { uv: [number, number]; layer: number; el: RefObject<HTMLElement | null> };
  /** painted story figures to stand in this scene */
  figures?: FigureDef[];
}

const MOOD_TINT: Record<string, [number, number, number]> = {
  calm: [1, 1, 1],
  triumph: [1.04, 1.02, 0.96],
  tense: [0.9, 0.94, 1.06],
  chaos: [1.06, 0.97, 1.06],
  dread: [0.98, 0.84, 0.84],
};

function frameK(aspect: number) {
  return Math.max(2 * TAN, (2 * TAN * aspect) / IA) * OVERSCAN;
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);
}

/* ---------------- camera ---------------- */

function CameraRig({ look, fx, reducedMotion, sceneId, showpiece }: { look: LayeredSceneProps['look']; fx: FxState; reducedMotion: boolean; sceneId: string; showpiece?: boolean }) {
  const { camera, clock, size } = useThree();
  const smooth = useRef(new THREE.Vector2());
  const introAt = useRef(clock.elapsedTime);
  const target = useMemo(() => new THREE.Vector3(), []);
  useEffect(() => {
    introAt.current = clock.elapsedTime;
  }, [sceneId, clock]);
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    if (reducedMotion) {
      camera.position.set(0, 0, 0);
      camera.lookAt(0, 0, -8);
      return;
    }
    smooth.current.lerp(new THREE.Vector2(look.current.x, look.current.y), 1 - Math.exp(-delta * 3.2));
    // Portrait phones see a narrow slice of the plate, so the same travel reads as a much bigger shift.
    // Scale lateral travel by the aspect ratio so the near plane never moves more than ~6% of the view.
    const aspectClamp = Math.min(1, Math.max(0.35, size.width / Math.max(1, size.height) / 1.6));
    const amp = (showpiece ? LOOK_AMP_SHOWPIECE : LOOK_AMP) * aspectClamp * 0.92;
    const idleX = (Math.sin(t * 0.13) * 0.12 + Math.sin(t * 0.071) * 0.05) * aspectClamp;
    const idleY = Math.sin(t * 0.11) * 0.05;
    const breathe = (0.5 + 0.5 * Math.sin(t * 0.09)) * (showpiece ? 0.45 : 0.25);
    const intro = 1 - easeOutCubic((t - introAt.current) / 3.2);
    fx.push *= Math.exp(-delta * 1.1);
    const x = idleX + smooth.current.x * amp;
    const y = idleY + smooth.current.y * amp * 0.5;
    const z = -breathe - intro * 0.8 - fx.push;
    camera.position.set(x, y, z);
    target.set(x * 0.12, y * 0.12, -8);
    camera.lookAt(target);
    camera.rotation.z += Math.sin(t * 0.05) * 0.004;
  });
  return null;
}

/* ---------------- painted layers ---------------- */

interface LayerDef {
  url: string;
  fallback?: string;
  d: number;
  depth?: string | null;
  displace: number;
}

function PaintedLayer({ def, index, fx, tint, reducedMotion, look }: { def: LayerDef; index: number; fx: FxState; tint: [number, number, number]; reducedMotion: boolean; look: LayeredSceneProps['look'] }) {
  const { size } = useThree();
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          uTex: { value: blankTex() },
          uPrev: { value: blankTex() },
          uDepth: { value: blankTex() },
          uHasDepth: { value: 0 },
          uMix: { value: 1 },
          uLook: { value: new THREE.Vector2() },
          uDisplace: { value: def.displace },
          uTime: { value: 0 },
          uLight: { value: 1 },
          uTint: { value: new THREE.Vector3(1, 1, 1) },
          uImpact: { value: -1 },
          uImpactUv: { value: new THREE.Vector2(0.5, 0.55) },
          uRipple: { value: 1 },
          uDarken: { value: 0 },
        },
        vertexShader: layerVertex,
        fragmentShader: layerFragment,
        transparent: index > 0,
        depthWrite: false,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const apply = (tex: THREE.Texture) => {
      if (cancelled) return;
      const u = material.uniforms;
      const had = u.uTex.value.image && (u.uTex.value as THREE.Texture).image?.width > 1;
      u.uPrev.value = had ? u.uTex.value : tex;
      u.uTex.value = tex;
      u.uMix.value = reducedMotion || !had ? 1 : 0;
    };
    loadTex(def.url)
      .then(apply)
      .catch(() => (def.fallback ? loadTex(def.fallback).then(apply).catch(() => undefined) : undefined));
    return () => {
      cancelled = true;
    };
  }, [def.url, def.fallback, material, reducedMotion]);

  useEffect(() => {
    if (!def.depth) {
      material.uniforms.uHasDepth.value = 0;
      return;
    }
    let cancelled = false;
    loadTex(def.depth)
      .then((t) => {
        if (cancelled) return;
        t.colorSpace = THREE.NoColorSpace;
        material.uniforms.uDepth.value = t;
        material.uniforms.uHasDepth.value = 1;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [def.depth, material]);

  useFrame((state, delta) => {
    const u = material.uniforms;
    const t = state.clock.elapsedTime;
    u.uTime.value = t;
    if (u.uMix.value < 1) u.uMix.value = Math.min(1, u.uMix.value + delta * 1.4);
    u.uLight.value = fx.light.current;
    u.uTint.value.set(...tint);
    u.uRipple.value = reducedMotion ? 0 : 1;
    u.uLook.value.set(reducedMotion ? 0 : look.current.x, reducedMotion ? 0 : look.current.y);
    const arrive = fx.impactAt + Math.abs(def.d - IMPACT_D) * 0.07;
    u.uImpact.value = t - arrive;
    u.uImpactUv.value.copy(fx.impactUv);
  });

  const k = frameK(size.width / Math.max(1, size.height));
  const h = def.d * k;
  return (
    <mesh position={[0, 0, -def.d]} scale={[h * IA, h, 1]} material={material} renderOrder={index}>
      <planeGeometry args={[1, 1]} />
    </mesh>
  );
}

/* ---------------- candle lights ---------------- */

function CandleGlows({ lights, dists, fx, dread, reducedMotion }: { lights: SceneLight[]; dists: number[]; fx: FxState; dread: number; reducedMotion: boolean }) {
  const { size } = useThree();
  const group = useRef<THREE.Group>(null);
  const tex = useMemo(() => glowTexture(), []);
  const seeds = useMemo(() => lights.map(() => Math.random() * 100), [lights]);
  const warm = useMemo(() => new THREE.Color('#ffb04d'), []);
  const red = useMemo(() => new THREE.Color('#ff3a2a'), []);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const dreadN = Math.min(1, dread / 10);
    let sum = 0;
    group.current?.children.forEach((child, i) => {
      const m = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
      const s = seeds[i] ?? 0;
      const erratic = 1 + dreadN * 2.5;
      const f = reducedMotion
        ? 1
        : 0.78 + 0.14 * Math.sin(t * 9 * erratic + s) + 0.08 * Math.sin(t * 23 + s * 3) - (dreadN > 0.6 && Math.sin(t * 3 + s) > 0.93 ? 0.5 : 0);
      sum += f;
      m.opacity = Math.max(0, f) * (0.85 - dreadN * 0.45);
      m.color.copy(warm).lerp(red, dreadN * 0.8);
    });
    fx.flicker = lights.length ? sum / lights.length : 1;
    fx.light.current = (1 - dreadN * 0.32) * (0.95 + 0.05 * fx.flicker);
  });
  const k = frameK(size.width / Math.max(1, size.height));
  return (
    <group ref={group}>
      {lights.map((l, i) => {
        const d = (dists[Math.min(l.layer, dists.length - 1)] ?? 9) - 0.05;
        const h = d * k;
        const w = h * IA;
        const r = Math.max(l.size * w * 5, d * 0.05);
        return (
          <mesh key={i} position={[(l.x - 0.5) * w, (0.5 - l.y) * h, -d]} scale={[r, r, 1]} renderOrder={10 + l.layer}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial map={tex} transparent depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
          </mesh>
        );
      })}
    </group>
  );
}

/* ---------------- light shafts ---------------- */

const shaftFrag = /* glsl */ `
uniform float uTime;
uniform vec3 uColor;
uniform float uStrength;
varying vec2 vUv;
float hash(float n) { return fract(sin(n) * 43758.5453); }
void main() {
  float x = vUv.x;
  float beams = 0.0;
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    float c = 0.18 + fi * 0.16 + sin(uTime * 0.07 + fi * 2.1) * 0.025;
    float w = 0.025 + hash(fi * 7.3) * 0.05;
    beams += smoothstep(w, 0.0, abs(x - c)) * (0.45 + 0.55 * hash(fi * 3.1));
  }
  float fall = smoothstep(0.0, 0.25, vUv.y) * (1.0 - smoothstep(0.55, 1.0, 1.0 - vUv.y) * 0.0);
  float top = smoothstep(0.05, 0.95, vUv.y);
  float shimmer = 0.75 + 0.25 * sin(uTime * 0.6 + x * 20.0);
  float a = beams * top * fall * shimmer * uStrength;
  gl_FragColor = vec4(uColor * a, a);
  #include <colorspace_fragment>
}
`;

function LightShafts({ x, angle, color, strength, fx }: { x: number; angle: number; color?: string; strength: number; fx: FxState }) {
  const { size } = useThree();
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uColor: { value: new THREE.Color(color ?? '#ffcf8a') }, uStrength: { value: strength } },
        vertexShader: layerVertex,
        fragmentShader: shaftFrag,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    [color, strength],
  );
  useFrame((state) => {
    material.uniforms.uTime.value = state.clock.elapsedTime;
    material.uniforms.uStrength.value = strength * fx.light.current;
  });
  const d = 7.6;
  const h = d * frameK(size.width / Math.max(1, size.height));
  return (
    <mesh position={[(x - 0.5) * h * IA, h * 0.08, -d]} rotation={[0, 0, angle]} scale={[h * 0.9, h * 1.3, 1]} material={material} renderOrder={5}>
      <planeGeometry args={[1, 1]} />
    </mesh>
  );
}

/* ---------------- fog ---------------- */

/** Deterministic pseudo random so fog layout never reshuffles on a re-render. */
function seeded(n: number) {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/** Seconds for a wisp to cross the whole view, per plane (back, mid). Slow ambient drift. */
export const FOG_CROSS_SECONDS = [84, 52] as const;

/**
 * Two slow fog planes behind the characters. Time based (clock seconds, not frames), eased with a gentle
 * speed swell, wrapping fully off screen, and pinned to the camera so camera drift and tilt do not whip it
 * sideways. Layout is seeded, so React re-renders (typewriter text, HUD updates) never teleport it.
 */
function FogCards({ amount, color, reducedMotion, dists, showpiece }: { amount: number; color?: string; reducedMotion: boolean; dists: number[]; showpiece?: boolean }) {
  const { size, camera } = useThree();
  const fog = propArt('fog_wisp');
  const [tex, setTex] = useState<THREE.Texture | null>(null);
  const group = useRef<THREE.Group>(null);
  useEffect(() => {
    if (fog) loadTex(fog.url).then(setTex).catch(() => undefined);
  }, [fog?.url]);
  const back = dists[0] * 0.8;
  const mid = dists.length > 2 ? (dists[1] + dists[2]) / 2 : dists[dists.length - 1] + 1;
  const cards = useMemo(() => {
    const out: { d: number; y: number; phase: number; cross: number; dir: number; scale: number; order: number; plane: number }[] = [];
    [back, mid].forEach((d, j) => {
      for (let i = 0; i < 3; i++) {
        const r = seeded(j * 10 + i + 1);
        out.push({
          d,
          y: -0.2 - seeded(j * 10 + i + 50) * 0.18 + j * 0.04,
          phase: (i / 3 + r * 0.2) % 1,
          cross: (showpiece ? FOG_CROSS_SECONDS[j] * 1.15 : FOG_CROSS_SECONDS[j]) * (0.92 + r * 0.16),
          dir: j % 2 ? -1 : 1,
          scale: 0.7 + seeded(j * 10 + i + 90) * 0.5,
          order: j === 0 ? 1 : 3,
          plane: j,
        });
      }
    });
    return out;
  }, [back, mid]);
  useFrame((state) => {
    if (!group.current) return;
    const t = reducedMotion ? 0 : state.clock.elapsedTime;
    // follow the camera most of the way so tilt and drift barely move the fog relative to the view
    group.current.position.set(camera.position.x * 0.85, camera.position.y * 0.85, 0);
    const aspect = size.width / Math.max(1, size.height);
    group.current.children.forEach((child, i) => {
      const c = cards[i];
      const viewW = 2 * c.d * TAN * aspect;
      const cardW = c.d * frameK(aspect) * IA * 0.75 * c.scale;
      const span = viewW + cardW * 1.05;
      // eased: a slow +-12% speed swell instead of a constant conveyor belt
      const prog = t / c.cross + ((0.12 * 30) / (2 * Math.PI * c.cross)) * Math.sin((2 * Math.PI * t) / 30 + c.phase * 6);
      const p = (((c.phase + prog * c.dir) % 1) + 1) % 1;
      child.position.x = (p - 0.5) * span;
    });
  });
  if (!fog || !tex || amount <= 0) return null;
  const k = frameK(size.width / Math.max(1, size.height));
  return (
    <group ref={group}>
      {cards.map((c, i) => {
        const h = c.d * k;
        const w = h * IA * 0.75 * c.scale;
        return (
          <mesh key={i} position={[0, c.y * h, -c.d]} scale={[w, w / fog.aspect, 1]} renderOrder={c.order}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial map={tex} transparent depthWrite={false} opacity={amount * (c.plane === 0 ? 0.5 : 0.38)} color={color ?? '#f3e6d6'} toneMapped={false} />
          </mesh>
        );
      })}
    </group>
  );
}

/* ---------------- foreground props ---------------- */

function ForegroundProp({ prop, fx, reducedMotion, index }: { prop: PropPlacement; fx: FxState; reducedMotion: boolean; index: number }) {
  const { size } = useThree();
  const art = propArt(prop.id);
  const [tex, setTex] = useState<THREE.Texture | null>(null);
  const ref = useRef<THREE.Mesh>(null);
  useEffect(() => {
    if (art) loadTex(art.url).then(setTex).catch(() => undefined);
  }, [art?.url]);
  const d = 2.3 + index * 0.25;
  const vh = 2 * d * TAN * 1.02;
  const vw = vh * (size.width / Math.max(1, size.height));
  const w = vw * prop.width;
  const h = art ? w / art.aspect : w;
  const pos = useMemo((): [number, number] => {
    switch (prop.anchor) {
      case 'top-left': return [-vw / 2 + w * 0.42, vh / 2 - h * 0.4];
      case 'top-right': return [vw / 2 - w * 0.45, vh / 2 - h * 0.38];
      case 'bottom-left': return [-vw / 2 + w * 0.35, -vh / 2 + h * 0.38];
      case 'bottom-right': return [vw / 2 - w * 0.35, -vh / 2 + h * 0.38];
      case 'left': return [-vw / 2 + w * 0.3, 0];
      case 'right': return [vw / 2 - w * 0.3, 0];
      default: return [0, 0];
    }
  }, [prop.anchor, vw, vh, w, h]);
  useFrame((state) => {
    if (!ref.current) return;
    const t = reducedMotion ? 0 : state.clock.elapsedTime;
    ref.current.rotation.z = Math.sin(t * 0.6 + index) * (prop.anchor.startsWith('top') ? 0.025 : 0.006);
    const m = ref.current.material as THREE.MeshBasicMaterial;
    const l = 0.55 + 0.45 * fx.light.current * (0.9 + 0.1 * fx.flicker);
    m.color.setRGB(l, l * 0.94, l * 0.88);
    if (prop.anchor === 'center') ref.current.position.y = Math.sin(t * 0.3) * 0.08;
  });
  if (!art || !tex) return null;
  return (
    <mesh ref={ref} position={[pos[0], pos[1], -d]} scale={[w, h, 1]} renderOrder={30 + index}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial map={tex} transparent depthWrite={false} toneMapped={false} opacity={prop.anchor === 'center' ? 0.75 : 1} />
    </mesh>
  );
}

/* ---------------- card fly-in + burst ---------------- */

const burstVert = /* glsl */ `
attribute vec3 aDir;
uniform float uT;
varying float vA;
void main() {
  vec3 p = aDir * (uT * 2.2) * (1.0 - uT * 0.35);
  p.y -= uT * uT * 0.9;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = 22.0 * (1.0 - uT) * (4.0 / max(0.5, -mv.z));
  vA = 1.0 - uT;
}
`;
const burstFrag = /* glsl */ `
uniform vec3 uColor;
varying float vA;
void main() {
  float a = smoothstep(0.5, 0.0, length(gl_PointCoord - 0.5));
  gl_FragColor = vec4(uColor, a * vA);
  #include <colorspace_fragment>
}
`;

/** World position of a plate uv on the painted layer at distance d (layers fill the frame from the origin). */
function layerPoint(uv: [number, number] | undefined, d: number, aspect: number, out = new THREE.Vector3()): THREE.Vector3 {
  const [u, v] = uv ?? [0.5, 0.44];
  const h = d * frameK(aspect);
  return out.set((u - 0.5) * h * IA, (0.5 - v) * h, -d);
}

/** Point at depth `depth` in front of the camera on the line of sight to `p`, so it overlaps p on screen. */
function alongSight(cam: THREE.Vector3, p: THREE.Vector3, depth: number, out: THREE.Vector3): THREE.Vector3 {
  const k = depth / Math.max(0.5, cam.z - p.z);
  return out.copy(p).sub(cam).multiplyScalar(k).add(cam);
}

/** A painted story figure standing on its layer: same plane maths as the layers, so it parallaxes with them. */
function FigurePlane({ fig, d, tint, fx, reducedMotion }: { fig: FigureDef; d: number; tint: [number, number, number]; fx: FxState; reducedMotion: boolean }) {
  const [tex, setTex] = useState<THREE.Texture | null>(null);
  const mesh = useRef<THREE.Mesh>(null);
  const { size } = useThree();
  useEffect(() => {
    let live = true;
    loadTex(figureUrl(fig)).then((t) => live && setTex(t)).catch(() => {});
    return () => {
      live = false;
    };
  }, [fig.id]);
  const material = useMemo(() => new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, toneMapped: false }), []);
  useEffect(() => {
    material.map = tex;
    material.needsUpdate = true;
  }, [tex, material]);
  useFrame((state) => {
    // follow the mood tint and the candle flicker the layers get, and breathe very slightly
    const l = 0.92 * (fx.light?.current ?? 1);
    material.color.setRGB(tint[0] * l, tint[1] * l, tint[2] * l);
    if (mesh.current && !reducedMotion) mesh.current.scale.y = baseH * (1 + Math.sin(state.clock.elapsedTime * 1.1 + d) * 0.004);
  });
  const k = frameK(size.width / Math.max(1, size.height));
  const h = d * k;
  const baseH = fig.height * h;
  const w = baseH * fig.aspect;
  const x = (fig.feet[0] - 0.5) * h * IA;
  const y = (0.5 - fig.feet[1]) * h + baseH / 2;
  if (!tex) return null;
  return (
    <mesh ref={mesh} position={[x, y, -(d - 0.03)]} scale={[w, baseH, 1]} material={material} renderOrder={fig.layer + 0.5}>
      <planeGeometry args={[1, 1]} />
    </mesh>
  );
}

/** Moves a DOM pin so it stays on its target as the camera drifts and tilts. */
function TargetTracker({ uv, d, el }: { uv: [number, number]; d: number; el: RefObject<HTMLElement | null> }) {
  const p = useMemo(() => new THREE.Vector3(), []);
  const v = useMemo(() => new THREE.Vector3(), []);
  useFrame((state) => {
    const node = el.current;
    if (!node) return;
    layerPoint(uv, d, state.size.width / Math.max(1, state.size.height), p);
    v.copy(p).project(state.camera);
    const rx = (v.x + 1) / 2;
    const x = Math.min(0.92, Math.max(0.08, rx));
    const y = Math.min(0.92, Math.max(0.08, (1 - v.y) / 2));
    // a target outside the (narrow, phone) frame gets an edge arrow instead of a misleading ring
    const off = rx < 0.02 ? 'left' : rx > 0.98 ? 'right' : '';
    if (node.dataset.off !== off) node.dataset.off = off;
    node.style.left = `${((off === 'left' ? 0.02 : off === 'right' ? 0.98 : x) * 100).toFixed(2)}%`;
    node.style.top = `${(y * 100).toFixed(2)}%`;
  });
  return null;
}

function FlyingCard({ cardId, playKey, fx, reducedMotion, onImpact, dramatic, targetUv, targetD }: { cardId?: string; playKey?: string; fx: FxState; reducedMotion: boolean; onImpact?: () => void; dramatic?: boolean; targetUv?: [number, number]; targetD: number }) {
  const card = cardId ? getCardById(cardId) : undefined;
  const mesh = useRef<THREE.Mesh>(null);
  const burst = useRef<THREE.Points>(null);
  const startAt = useRef(-100);
  const impacted = useRef(true);
  const material = useMemo(() => new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, toneMapped: false, side: THREE.DoubleSide, color: new THREE.Color(0.82, 0.82, 0.82) }), []);
  const burstGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const n = 90;
    const dirs = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const v = new THREE.Vector3().randomDirection().multiplyScalar(0.4 + Math.random() * 0.8);
      dirs.set([v.x, v.y, v.z * 0.4], i * 3);
    }
    g.setAttribute('aDir', new THREE.BufferAttribute(dirs, 3));
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    return g;
  }, []);
  const burstMat = useMemo(
    () => new THREE.ShaderMaterial({ uniforms: { uT: { value: 1 }, uColor: { value: new THREE.Color('#ffd27a') } }, vertexShader: burstVert, fragmentShader: burstFrag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }),
    [],
  );
  const { clock } = useThree();
  const end = useMemo(() => new THREE.Vector3(0, 0, -IMPACT_D), []);
  const target = useMemo(() => new THREE.Vector3(), []);
  const impactUv = targetUv ? new THREE.Vector2(targetUv[0], 1 - targetUv[1]) : new THREE.Vector2(0.5, 0.56);

  useEffect(() => {
    if (!playKey || !card) return;
    fx.impactUv.copy(impactUv);
    const tex = getCardTexture(card, 40).clone();
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    material.map = tex;
    material.needsUpdate = true;
    if (reducedMotion) {
      onImpact?.();
      return;
    }
    startAt.current = clock.elapsedTime;
    impacted.current = false;
  }, [playKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((state) => {
    // aim through the camera at the target's own layer every frame, so drift and tilt never miss it
    layerPoint(targetUv, targetD, state.size.width / Math.max(1, state.size.height), target);
    alongSight(state.camera.position, target, IMPACT_D, end);
    const t = state.clock.elapsedTime - startAt.current;
    const dur = 0.72;
    if (mesh.current) {
      if (t < 0 || t > dur + 0.35) {
        mesh.current.visible = false;
      } else {
        mesh.current.visible = true;
        const p = easeOutCubic(t / dur);
        const cam = state.camera.position;
        const start = new THREE.Vector3(cam.x + 0.15, cam.y - 1.25, cam.z - 2.0);
        mesh.current.position.lerpVectors(start, end, p);
        mesh.current.position.y += Math.sin(p * Math.PI) * 0.6;
        mesh.current.rotation.set(-0.5 * (1 - p), p * Math.PI * 2, (1 - p) * 0.4);
        const s = 0.85 - p * 0.15;
        mesh.current.scale.set(s, s * 1.41, 1);
        material.opacity = t > dur ? 1 - (t - dur) / 0.35 : 1;
      }
    }
    if (!impacted.current && t >= dur) {
      impacted.current = true;
      fx.impactAt = state.clock.elapsedTime;
      fx.impactUv.copy(impactUv);
      fx.push = dramatic ? 1.15 : 0.6;
      onImpact?.();
    }
    const bt = (state.clock.elapsedTime - fx.impactAt) / 1.1;
    if (burst.current) {
      burst.current.position.copy(end);
      burst.current.visible = bt >= 0 && bt < 1 && !reducedMotion;
      burstMat.uniforms.uT.value = Math.min(1, Math.max(0, bt));
    }
  });

  return (
    <>
      <mesh ref={mesh} material={material} visible={false} renderOrder={40}>
        <planeGeometry args={[0.95, 0.95]} />
      </mesh>
      <points ref={burst} geometry={burstGeo} material={burstMat} visible={false} renderOrder={41} frustumCulled={false} />
    </>
  );
}

/* ---------------- effect overlays in depth ---------------- */

function Overlays3D({ ids, playKey, fx, reducedMotion, targetUv, targetD }: { ids: string[]; playKey?: string; fx: FxState; reducedMotion: boolean; targetUv?: [number, number]; targetD: number }) {
  const [texes, setTexes] = useState<(THREE.Texture | null)[]>([]);
  const group = useRef<THREE.Group>(null);
  const { clock } = useThree();
  const armedAt = useRef(-1);
  // overlays stamp onto the target: on the line of sight to it, on their own nearer plane
  const target = useMemo(() => new THREE.Vector3(), []);
  const at = useMemo(() => new THREE.Vector3(), []);
  useEffect(() => {
    armedAt.current = reducedMotion ? -1 : clock.elapsedTime;
  }, [playKey, reducedMotion, clock]);
  useEffect(() => {
    let cancelled = false;
    Promise.all(ids.map((id) => {
      const u = overlayUrl(id);
      return u ? loadTex(u).catch(() => null) : Promise.resolve(null);
    })).then((t) => !cancelled && setTexes(t));
    return () => {
      cancelled = true;
    };
  }, [ids.join(','), playKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const d = 4.7;
  useFrame((state) => {
    if (!group.current) return;
    layerPoint(targetUv, targetD, state.size.width / Math.max(1, state.size.height), target);
    alongSight(state.camera.position, target, 4.7, at);
    group.current.position.set(at.x, at.y, at.z + d);
    const t = state.clock.elapsedTime - fx.impactAt;
    group.current.children.forEach((child, i) => {
      const waiting = !reducedMotion && fx.impactAt < armedAt.current;
      const local = reducedMotion ? 10 : waiting ? -1 : t - i * 0.12;
      const pop = local < 0 ? 0 : local < 0.5 ? 1.25 * easeOutCubic(local / 0.35) - 0.25 * Math.max(0, (local - 0.35) / 0.15) : 1;
      const s = Math.max(0, pop);
      child.scale.setScalar(s * 1.25);
      child.position.y = 0.1 + (reducedMotion ? 0 : Math.sin(state.clock.elapsedTime * 1.3 + i) * 0.05);
      ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = Math.min(1, s) * 0.95;
    });
  });
  const n = Math.min(ids.length, 3);
  return (
    <group ref={group}>
      {texes.slice(0, 3).map((tex, i) =>
        tex ? (
          <mesh key={`${ids[i]}-${playKey}`} position={[(i - (n - 1) / 2) * 0.9, 0.1, -d]} renderOrder={35}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial map={tex} transparent depthWrite={false} toneMapped={false} />
          </mesh>
        ) : null,
      )}
    </group>
  );
}

/* ---------------- scene ---------------- */

export function LayeredScene(props: LayeredSceneProps) {
  const { sceneId, mood, dread, reducedMotion, quality, look, playKey, playedCardId, overlayIds, dramatic, showpiece, onImpact, targetUv, targetLayer = 2, track, figures } = props;
  const fx = useMemo<FxState>(() => ({ impactAt: -100, impactUv: new THREE.Vector2(0.5, 0.55), push: 0, light: { current: 1 }, flicker: 1 }), []);
  const set = layerSet(sceneId, mood);
  const fxCfg = sceneFx(sceneId, mood);
  const tint = MOOD_TINT[mood] ?? MOOD_TINT.calm;

  const layers: LayerDef[] = useMemo(() => {
    if (set) {
      // Cutout layers move as rigid planes: per-pixel depth displacement inside a cutout shears figures whose
      // head and body got different depth values, which read as a slice at the shoulder.
      if (quality.layers === 'two') return [{ url: set.twoLayer[0], d: DIST4[0], displace: 0 }, { url: set.twoLayer[1], d: 9, displace: 0 }];
      const n = set.urls.length;
      const dists = n === 4 ? DIST4 : set.urls.map((_, i) => DIST4[0] * Math.pow(DIST4[3] / DIST4[0], i / Math.max(1, n - 1)));
      return set.urls.map((url, i) => ({ url, d: dists[i], depth: null, displace: 0 }));
    }
    // no layers (new chapter or missing art): one strongly displaced plate
    const fallback = scenePlaceholderSvg(sceneId, mood);
    return [{ url: backgroundUrl(sceneId, mood) ?? fallback, fallback, d: 9, depth: depthUrl(sceneId), displace: 0.07 }];
  }, [set?.urls.join(','), quality.layers, sceneId, mood]); // eslint-disable-line react-hooks/exhaustive-deps

  // warm the cache for the other moods of this scene so crossfades are instant
  useEffect(() => {
    if (!set) return;
    ['tense', 'chaos', 'dread'].forEach((m) => {
      const s = layerSet(sceneId, m);
      if (s) preload(quality.layers === 'two' ? s.twoLayer : s.urls);
    });
  }, [sceneId]); // eslint-disable-line react-hooks/exhaustive-deps

  const dists = useMemo(() => layers.map((l) => l.d), [layers]);
  // distance of the plane a given painted layer ended up on in this quality tier
  const layerD = (k: number) => (dists.length >= 4 ? dists[Math.min(3, Math.max(0, k))] : dists.length === 2 ? dists[k === 0 ? 0 : 1] : dists[0]);

  return (
    <>
      <CameraRig look={look} fx={fx} reducedMotion={reducedMotion} sceneId={sceneId} showpiece={showpiece} />
      {layers.map((def, i) => (
        <PaintedLayer key={`${sceneId}-${i}-${layers.length}`} def={def} index={i} fx={fx} tint={tint} reducedMotion={reducedMotion} look={look} />
      ))}
      {set && figures?.map((f) => <FigurePlane key={f.id} fig={f} d={layerD(f.layer)} tint={tint} fx={fx} reducedMotion={reducedMotion} />)}
      {quality.shafts && fxCfg.shafts && (
        <LightShafts x={fxCfg.shafts.x} angle={fxCfg.shafts.angle} color={fxCfg.shafts.color} strength={mood === 'dread' ? 0.12 : mood === 'tense' ? 0.22 : 0.32} fx={fx} />
      )}
      <FogCards amount={fxCfg.fog} color={mood === 'dread' ? '#b98a8a' : mood === 'tense' ? '#c6cde6' : fxCfg.fogColor} reducedMotion={reducedMotion} dists={dists} showpiece={showpiece} />
      {set && <CandleGlows lights={quality.tier === 'low' ? set.lights.slice(0, 4) : set.lights} dists={dists.length === 2 ? [dists[0], dists[1], dists[1], dists[1]] : dists} fx={fx} dread={dread} reducedMotion={reducedMotion} />}
      {!reducedMotion && fxCfg.particles.map((k) => <Particles key={k} kind={k} count={quality.particles} reducedMotion={reducedMotion} light={fx.light} />)}
      {fxCfg.props.slice(0, quality.tier === 'low' ? 1 : 3).map((p, i) => (
        <ForegroundProp key={`${sceneId}-${p.id}-${p.anchor}`} prop={p} fx={fx} reducedMotion={reducedMotion} index={i} />
      ))}
      <FlyingCard cardId={playedCardId} playKey={playKey} fx={fx} reducedMotion={reducedMotion} onImpact={onImpact} dramatic={dramatic} targetUv={targetUv} targetD={layerD(targetLayer)} />
      {overlayIds && overlayIds.length > 0 && <Overlays3D ids={overlayIds} playKey={playKey} fx={fx} reducedMotion={reducedMotion} targetUv={targetUv} targetD={layerD(targetLayer)} />}
      {track && <TargetTracker uv={track.uv} d={layerD(track.layer)} el={track.el} />}
      <PostFx fx={fx} enabled={quality.post} reducedMotion={reducedMotion} />
    </>
  );
}

/* ---------------- post ---------------- */


function PostFx({ fx, enabled, reducedMotion }: { fx: FxState; enabled: boolean; reducedMotion: boolean }) {
  const ca = useRef<{ offset: THREE.Vector2 } | null>(null);
  const offset = useMemo(() => new THREE.Vector2(0.0004, 0.0004), []);
  useFrame((state) => {
    const t = state.clock.elapsedTime - fx.impactAt;
    const g = reducedMotion ? 0 : t >= 0 && t < 0.55 ? Math.pow(1 - t / 0.55, 2) : 0;
    const v = 0.0003 + g * 0.0038;
    if (ca.current) ca.current.offset.set(v, v * 0.6);
  });
  if (!enabled) return null;
  return (
    <EffectComposer multisampling={0}>
      <Bloom intensity={0.85} luminanceThreshold={0.6} luminanceSmoothing={0.25} mipmapBlur />
      <ChromaticAberration ref={ca as never} offset={offset} blendFunction={BlendFunction.NORMAL} radialModulation={false} modulationOffset={0} />
      <Vignette offset={0.28} darkness={0.62} />
    </EffectComposer>
  );
}
